import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import { api } from "@/lib/api";

const PROGRESS_KEY = "qr_audit_progress_v1";
// Keep saved progress for 7 days
const PROGRESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function loadProgress() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage?.getItem(PROGRESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - (parsed.savedAt || 0) > PROGRESS_TTL_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function saveProgress(state) {
  try {
    window.localStorage?.setItem(PROGRESS_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch (e) {
    /* ignore quota / private-mode errors */
  }
}

function clearProgress() {
  try {
    window.localStorage?.removeItem(PROGRESS_KEY);
  } catch (e) {
    /* ignore */
  }
}

export default function Quiz() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [urlInput, setUrlInput] = useState("");
  const [urlChecking, setUrlChecking] = useState(false);
  const [urlCheck, setUrlCheck] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resumePrompt, setResumePrompt] = useState(null); // {current, answers, urlInput, urlCheck}

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/questions");
        setQuestions(res.data.questions || []);
        const saved = loadProgress();
        if (saved && saved.answers && Object.keys(saved.answers).length > 0) {
          setResumePrompt(saved);
        }
      } catch (e) {
        toast.error("Couldn't load the quiz. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Persist progress on any answer / step / url change
  useEffect(() => {
    if (loading || resumePrompt) return; // don't overwrite pending resume
    if (Object.keys(answers).length === 0 && !urlCheck && !urlInput) return;
    saveProgress({ current, answers, urlInput, urlCheck });
  }, [current, answers, urlInput, urlCheck, loading, resumePrompt]);

  const acceptResume = () => {
    if (!resumePrompt) return;
    setCurrent(resumePrompt.current || 0);
    setAnswers(resumePrompt.answers || {});
    setUrlInput(resumePrompt.urlInput || "");
    setUrlCheck(resumePrompt.urlCheck || null);
    setResumePrompt(null);
    toast.success("Resumed where you left off.");
  };

  const declineResume = () => {
    clearProgress();
    setResumePrompt(null);
    toast.message("Starting fresh.");
  };

  const q = questions[current];
  const total = questions.length;
  const progress = total ? Math.round(((current + 1) / total) * 100) : 0;

  const canAdvance = useMemo(() => {
    if (!q) return false;
    if (q.type === "url") return true; // optional
    if (q.type === "multi") {
      const v = answers[q.id];
      return Array.isArray(v) && v.length > 0;
    }
    return !!answers[q.id];
  }, [q, answers]);

  const setSingle = (value) => setAnswers((a) => ({ ...a, [q.id]: value }));
  const toggleMulti = (value) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[q.id]) ? a[q.id] : [];
      const opt = q.options.find((o) => o.value === value);
      const isExclusive = opt?.exclusive;
      // Toggling an exclusive option (e.g. "None of these") clears the others.
      if (isExclusive) {
        return { ...a, [q.id]: cur.includes(value) ? [] : [value] };
      }
      // Toggling any non-exclusive option removes any exclusive option that's set.
      const withoutExclusive = cur.filter((x) => {
        const o = q.options.find((oo) => oo.value === x);
        return !o?.exclusive;
      });
      const next = withoutExclusive.includes(value)
        ? withoutExclusive.filter((x) => x !== value)
        : [...withoutExclusive, value];
      return { ...a, [q.id]: next };
    });

  const handleUrlCheck = async () => {
    if (!urlInput.trim()) {
      toast.error("Enter a URL first.");
      return;
    }
    setUrlChecking(true);
    try {
      const res = await api.post("/url-check", { url: urlInput.trim() });
      setUrlCheck(res.data);
      if (res.data.reachable) toast.success("URL check complete.");
      else toast.error("Couldn't reach that URL. It won't be used in your score.");
    } catch (e) {
      toast.error("URL check failed. You can skip this question.");
    } finally {
      setUrlChecking(false);
    }
  };

  const next = () => {
    if (current < total - 1) setCurrent(current + 1);
    else finalize();
  };

  const back = () => {
    if (current > 0) setCurrent(current - 1);
  };

  const finalize = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers,
        url_check: urlCheck && urlCheck.reachable ? urlCheck : null,
      };
      const res = await api.post("/submit", payload);
      const { submission_id } = res.data;
      clearProgress();
      navigate(`/report/${submission_id}?revealed=1`);
    } catch (e) {
      if (e?.response?.status === 422) {
        const detail = e.response.data?.detail;
        const missing = (detail?.missing || []).concat(detail?.empty_multi || []);
        toast.error(
          missing.length
            ? `Please answer every question (${missing.length} left).`
            : "Please complete every question before submitting."
        );
        // Jump user back to the first missing question
        if (missing.length && questions.length) {
          const firstIdx = questions.findIndex((q) => missing.includes(q.id));
          if (firstIdx >= 0) setCurrent(firstIdx);
        }
      } else {
        toast.error("Couldn't submit the audit. Please try again.");
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C6F84C]" />
      </div>
    );
  }

  if (resumePrompt) {
    const answeredCount = Object.keys(resumePrompt.answers || {}).length;
    return (
      <div className="grain min-h-screen flex flex-col">
        <Header variant="quiz" />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#121212] p-8 text-center" data-testid="resume-prompt">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">Resume your audit?</div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight">We saved your progress.</h2>
            <p className="mt-3 text-zinc-400">
              You left off with {answeredCount} of {questions.length || 12} questions answered. Pick up where you were, or start fresh.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={acceptResume}
                data-testid="resume-accept-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-5 py-3 transition-colors duration-200"
              >
                <RotateCcw className="w-4 h-4" /> Resume
              </button>
              <button
                type="button"
                onClick={declineResume}
                data-testid="resume-decline-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-medium px-5 py-3 transition-colors duration-200"
              >
                Start fresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!q) return null;

  return (
    <div className="grain min-h-screen flex flex-col">
      <Header variant="quiz" />

      {/* Progress bar */}
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
            <span data-testid="quiz-progress-label">Question {current + 1} of {total}</span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C6F84C] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="quiz-progress-bar"
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex items-start justify-center py-12">
        <div className="max-w-2xl w-full mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.28 }}
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">
                {q.category?.replace("_", " ") || ""}
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="quiz-question-text">
                {q.text}
              </h1>
              {q.type === "multi" && (
                <p className="mt-2 text-sm text-zinc-500">Select all that apply.</p>
              )}
              {q.type === "url" && (
                <p className="mt-2 text-sm text-zinc-500">Optional — you can skip this.</p>
              )}

              <div className="mt-8 space-y-3">
                {q.type === "single" &&
                  q.options.map((opt) => {
                    const selected = answers[q.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSingle(opt.value)}
                        data-selected={selected}
                        data-testid={`quiz-option-${q.id}-${opt.value}`}
                        className="option-card w-full text-left px-5 py-4 rounded-xl flex items-center justify-between"
                      >
                        <span className="font-medium text-white">{opt.label}</span>
                        {selected && <Check className="w-5 h-5 text-[#C6F84C]" />}
                      </button>
                    );
                  })}

                {q.type === "multi" &&
                  q.options.map((opt) => {
                    const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                    const selected = cur.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleMulti(opt.value)}
                        data-selected={selected}
                        data-testid={`quiz-option-${q.id}-${opt.value}`}
                        className="option-card w-full text-left px-5 py-4 rounded-xl flex items-center justify-between"
                      >
                        <span className="font-medium text-white">{opt.label}</span>
                        <div
                          className={`w-5 h-5 rounded-md border ${selected ? "bg-[#C6F84C] border-[#C6F84C]" : "border-zinc-600"} flex items-center justify-center`}
                        >
                          {selected && <Check className="w-3.5 h-3.5 text-black" />}
                        </div>
                      </button>
                    );
                  })}

                {q.type === "url" && (
                  <div className="option-card rounded-xl p-5">
                    <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
                      Your QR destination URL
                    </label>
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <input
                        type="url"
                        inputMode="url"
                        placeholder="https://example.com/menu"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        data-testid="quiz-url-input"
                        className="flex-1 bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#C6F84C] transition-colors duration-200"
                      />
                      <button
                        type="button"
                        onClick={handleUrlCheck}
                        disabled={urlChecking || !urlInput.trim()}
                        data-testid="quiz-url-check-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#C6F84C] hover:bg-[#B8FF3D] disabled:opacity-50 text-black font-bold px-5 py-3 transition-colors duration-200"
                      >
                        {urlChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run check"}
                      </button>
                    </div>
                    {urlCheck && urlCheck.reachable && (
                      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                        <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                          <div className="text-xs uppercase tracking-widest text-zinc-500">HTTPS</div>
                          <div className={`mt-1 font-bold ${urlCheck.https ? "text-[#C6F84C]" : "text-red-400"}`}>
                            {urlCheck.https ? "Yes" : "No"}
                          </div>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                          <div className="text-xs uppercase tracking-widest text-zinc-500">Mobile viewport</div>
                          <div className={`mt-1 font-bold ${urlCheck.mobile_viewport ? "text-[#C6F84C]" : "text-red-400"}`}>
                            {urlCheck.mobile_viewport ? "Yes" : "Missing"}
                          </div>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                          <div className="text-xs uppercase tracking-widest text-zinc-500">Response</div>
                          <div className="mt-1 font-mono font-bold text-white">
                            {urlCheck.ttfb_ms}ms
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Nav */}
              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button"
                  onClick={back}
                  disabled={current === 0}
                  data-testid="quiz-back-btn"
                  className="inline-flex items-center gap-2 text-zinc-400 hover:text-white disabled:opacity-30 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="button"
                  onClick={next}
                  disabled={(!canAdvance && q.type !== "url") || submitting}
                  data-testid="quiz-next-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold px-6 py-3 transition-colors duration-200"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Scoring…
                    </>
                  ) : current === total - 1 ? (
                    <>See my score <ArrowRight className="w-4 h-4" /></>
                  ) : (
                    <>
                      {q.type === "url" && !urlCheck ? "Skip" : "Next"} <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
